import React from "react";
import {
  CartesianGrid,
  Bar,
  BarChart,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell
} from "recharts";
import _ from "lodash";

import {
  ThemeType,
  formatNumber
} from "src/library/utils";

import { GraphProps } from "./Graph";
import GraphToolTip from "./GraphToolTip";
import {
  determineInterval,
  formattedXAxisDate,
  getStroke
} from "./utils";

export interface GraphBarChartProps extends GraphProps {
  theme: ThemeType | null;
}

// Generates a Bar Chart
const GraphBarChart: React.ReactFCWithChildren<GraphBarChartProps> = ({
  data,
  graphMetrics,
  theme,
  xAxis,
  toolTipValueType,
  containerWidth,
  stacked = false,
  hideLegend = false
}) => {
  const interval = React.useMemo(() => {
    return determineInterval(data.length);
  }, [ data ]);

  return (
    <ResponsiveContainer
      width="100%"
      height="100%"
    >
      <BarChart
        data={data}
        margin={{
          top: 5,
          right: 30,
          left: 0,
          bottom: 5
        }}
      >
        <CartesianGrid stroke={theme ? theme.colors.lightestGrey : "#eee"} />

        <XAxis
          angle={containerWidth && containerWidth < 300 ? -15 : 0}
          dataKey={xAxis.dataKey}
          interval={interval}
          tickFormatter={tickItem => tickItem instanceof Date ? formattedXAxisDate(tickItem, xAxis.dateFormat) : _.startCase(tickItem.replace(/[_-]/g, " "))}
        />

        <YAxis tickFormatter={tickItem => typeof tickItem === "number" ? formatNumber(tickItem, "short") : tickItem} />

        <Tooltip content={<GraphToolTip valueType={toolTipValueType} />} />

        {!hideLegend && (
          <Legend formatter={item => typeof item === "string" && _.startCase(item.replace(/[_-]/g, " "))} />
        )}

        {theme && (
          graphMetrics.map((metric, iteration) => (
            <Bar
              key={metric.dataKey}
              dataKey={metric.dataKey}
              barSize={20}
              fill={getStroke(iteration, theme)}
              // hard-coded-id as currently rendering one bar chart in this component
              stackId={stacked ? "hard-coded-id" : undefined}
            >
              {hideLegend && data.map((_entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={getStroke(index, theme)}
                />
              ))}
            </Bar>
          ))
        )}

      </BarChart>
    </ResponsiveContainer>
  );
};

export default GraphBarChart;
